import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CopySelectField } from "@/components/catalog/CopySelectField";
import type { ItemType } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { Collection } from "@/lib/api/collections";
import type { BookCondition } from "@/lib/api/book-conditions";
import type { ItemStatus } from "@/lib/api/item-statuses";
import type { Branch } from "@/lib/api/branches";

interface ExistingCopyEditorFieldsProps {
  itemId: string;
  disabled: boolean;
  branchId: string;
  locationId: string;
  collectionId: string;
  itemTypeId: string;
  conditionCode: string;
  statusCode: string;
  autoRenewalDisabled: boolean;
  branches: Branch[];
  itemTypes: ItemType[];
  locations: Location[];
  collections: Collection[];
  conditions: BookCondition[];
  itemStatuses: ItemStatus[];
  onBranchChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onCollectionChange: (value: string) => void;
  onItemTypeChange: (value: string) => void;
  onConditionChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onAutoRenewalChange: (value: boolean) => void;
}

export function ExistingCopyEditorFields({
  itemId,
  disabled,
  branchId,
  locationId,
  collectionId,
  itemTypeId,
  conditionCode,
  statusCode,
  autoRenewalDisabled,
  branches,
  itemTypes,
  locations,
  collections,
  conditions,
  itemStatuses,
  onBranchChange,
  onLocationChange,
  onCollectionChange,
  onItemTypeChange,
  onConditionChange,
  onStatusChange,
  onAutoRenewalChange,
}: ExistingCopyEditorFieldsProps) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <CopySelectField
          label="Branch"
          value={branchId}
          disabled={disabled}
          onChange={onBranchChange}
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
        />
        <CopySelectField
          label="Location"
          value={locationId}
          disabled={disabled}
          onChange={onLocationChange}
          options={locations.map((l) => ({ value: l.id, label: l.name }))}
        />
        <CopySelectField
          label="Collection"
          value={collectionId}
          disabled={disabled}
          onChange={onCollectionChange}
          options={collections.map((c) => ({ value: c.id, label: c.name }))}
        />
        <CopySelectField
          label="Item Type"
          value={itemTypeId}
          disabled={disabled}
          onChange={onItemTypeChange}
          options={itemTypes.map((t) => ({ value: t.id, label: t.name }))}
        />
        <CopySelectField
          label="Condition"
          value={conditionCode}
          disabled={disabled}
          onChange={onConditionChange}
          options={conditions.map((c) => ({ value: c.code, label: c.label }))}
        />
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusCode} onValueChange={onStatusChange} disabled={disabled}>
            <SelectTrigger className="mt-1 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {itemStatuses.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-200">
        <div className="space-y-0.5">
          <Label htmlFor={`auto-renew-off-${itemId}`} className="text-xs font-medium">
            Disable automated renewal (this copy)
          </Label>
          <p className="text-xs text-gray-500 max-w-xl">
            When enabled, scheduled auto-renew skips this barcode.
          </p>
        </div>
        <Switch
          id={`auto-renew-off-${itemId}`}
          checked={autoRenewalDisabled}
          onCheckedChange={onAutoRenewalChange}
          disabled={disabled}
        />
      </div>
    </>
  );
}
